import React, { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Paper, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import GridOnIcon from '@mui/icons-material/GridOn';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import AddModeratorIcon from '@mui/icons-material/AddModerator';

import { FeedKey, GroupContentApprovalType, GroupFragment, useGetGroupFollowersQuery } from 'api';
import { FeedViewOptions } from 'types/app';
import { FollowToggle, GroupAvatar, PrivateGroupIcon } from 'components';
import { useCapabilities, useFollowed, useReleases } from 'hooks';

import Tabs from './Tabs';
import { isRequiredGroup } from '../../helpers';
import theme from 'theme';

interface Props {
  group: GroupFragment;
  activeView?: FeedViewOptions;
  onViewChange?: () => void;
}

const styles = {
  block: {
    width: 2,
    height: 2,
    borderRadius: '50%',
  },
  button: {
    minWidth: 150,
  },
  container: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    paddingRight: 2,
  },
  editButton: {
    marginLeft: 2,
  },
  iconButton: {
    '&:hover': {
      backgroundColor: 'common.white',
    },
  },
} as const;

const DesktopBanner: FC<Props> = ({ group, activeView, onViewChange }) => {
  const navigate = useNavigate();
  const releases = useReleases();
  const { t } = useTranslation();
  const { isAdmin } = useCapabilities();
  const { required } = useFollowed();
  const { group_id } = useParams<{ group_id: string }>();
  const { needs } = useCapabilities();

  const has4EyeModeration = releases.includes('4EyeModeration');
  const hasGridView = releases.includes('GridViewWeb');
  const canEdit = needs('Access.Moderator', group_id) || isAdmin;

  const isRequired = isRequiredGroup(group, required);
  const refetchQueryKey = useGetGroupFollowersQuery.getKey({
    data: { id: group.id, limit: 10, feed: FeedKey.Group },
  });

  function renderTopRow() {
    return (
      <Grid container alignItems="center" spacing={2}>
        <Grid>
          <GroupAvatar square group={group} size={68} />
        </Grid>
        <Grid>
          <PrivateGroupIcon group={group} type="title" />
        </Grid>
        {has4EyeModeration && group.content_approval_type === GroupContentApprovalType.FourEyes && (
          <Grid display="flex" justifyContent="center" alignItems="center">
            <AddModeratorIcon htmlColor={group.color_map?.hex} fontSize="medium" />
          </Grid>
        )}
        <Grid xs>
          <Typography title={group.title} color="textPrimary" variant="h4" noWrap>
            {group.title}
          </Typography>
        </Grid>
        <Grid>
          <Typography noWrap>{group.department_title}</Typography>
        </Grid>
        <Grid>
          <Typography noWrap>{group.location_title}</Typography>
        </Grid>
        <Grid>
          <FollowToggle source={group} type={FeedKey.Group} refetchQueryKeys={[refetchQueryKey]}>
            {({ isFollowing, onClick }) => {
              return (
                <Grid container spacing={1} justifyContent="center" alignItems="center">
                  {!isRequired && !isFollowing && (
                    <Grid>
                      <Button onClick={onClick} color="primary" variant="contained">
                        {t('common:join')}
                      </Button>
                    </Grid>
                  )}
                  {!isRequired && isFollowing && (
                    <Grid>
                      <Button variant="contained" onClick={onClick}>
                        {t('common:leave')}
                      </Button>
                    </Grid>
                  )}
                  {canEdit && (
                    <Grid>
                      <Button
                        variant="contained"
                        onClick={() => navigate(`/group/${group_id}/edit`)}
                      >
                        {t('common:edit')}
                      </Button>
                    </Grid>
                  )}
                  {hasGridView && activeView && (
                    <Grid>
                      <IconButton
                        id="feed-view-switch"
                        onClick={onViewChange}
                        sx={styles.iconButton}
                        size="large"
                      >
                        {activeView === FeedViewOptions.List ? <GridOnIcon /> : <ViewDayIcon />}
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              );
            }}
          </FollowToggle>
        </Grid>
      </Grid>
    );
  }

  return (
    <Paper>
      <Grid container alignItems="center" sx={styles.container}>
        <Grid xs>{renderTopRow()}</Grid>
      </Grid>
      <Grid container>
        <Grid>
          <Tabs group={group} />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default DesktopBanner;
